import { Formik } from 'formik';
import { useRouter } from '@uirouter/react';

import { ContainerInstanceFormValues } from '@/react/azure/types';
import { useCurrentUser } from '@/react/hooks/useUser';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { CreateContainerInstanceInnerForm } from '@/react/azure/container-instances/CreateView/CreateContainerInstanceInnerForm';

import { useSubscriptions } from '../../queries/useSubscriptions';
import { useResourceGroups } from '../../queries/useResourceGroups';
import { useProviders } from '../../queries/useProvider';
import { useVirtualNetworks } from '../../queries/useVirtualNetworks';
import { useContainerGroups } from '../../queries/useContainerGroups';
import { useContainerGroupRows } from '../useContainerGroupRows';

import { validationSchema } from './CreateContainerInstanceForm.validation';
import { useFormState } from './useLoadFormState';
import { useCreateContainerInstance } from './useCreateContainerInstance';

export function CreateContainerInstanceForm({
  defaultValues,
}: {
  defaultValues?: Partial<ContainerInstanceFormValues>;
}) {
  const environmentId = useEnvironmentId();
  const { isPureAdmin } = useCurrentUser();

  // queries to build form state
  const subscriptionsQuery = useSubscriptions(environmentId);
  const subscriptions = subscriptionsQuery.data ?? [];
  const resourceGroupsQuery = useResourceGroups(environmentId, subscriptions);
  const resourceGroups = resourceGroupsQuery.data;
  const providersQuery = useProviders(environmentId, subscriptions);
  const providers = providersQuery.data;
  const virtualNetworksQuery = useVirtualNetworks(
    environmentId,
    subscriptions?.[0]?.subscriptionId || ''
  );
  const virtualNetworks = virtualNetworksQuery.data || [];

  const containerGroupsQuery = useContainerGroups(
    environmentId,
    subscriptionsQuery.data,
    subscriptionsQuery.isSuccess
  );
  const allResourceGroups = Object.values(resourceGroupsQuery.data).flat();
  const containerGroupRows = useContainerGroupRows(
    containerGroupsQuery.data,
    allResourceGroups,
    []
  );

  const {
    initialValues,
    subscriptionOptions,
    locationOptions,
    resourceGroupOptions,
  } = useFormState(
    subscriptions,
    resourceGroups,
    providers,
    virtualNetworks,
    defaultValues
  );

  const router = useRouter();

  const { mutateAsync } = useCreateContainerInstance(
    resourceGroups,
    environmentId
  );

  return (
    <Formik<ContainerInstanceFormValues>
      initialValues={initialValues}
      validationSchema={() => validationSchema(isPureAdmin, containerGroupRows)}
      onSubmit={onSubmit}
      validateOnMount
      validateOnChange
      enableReinitialize
    >
      {(formikProps) => (
        <CreateContainerInstanceInnerForm
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...formikProps}
          resourceGroupOptions={resourceGroupOptions}
          subscriptionOptions={subscriptionOptions}
          locationOptions={locationOptions}
          isSubscriptionsLoading={subscriptionsQuery.isLoading}
          isResourceGroupsLoading={resourceGroupsQuery.isLoading}
          isProvidersLoading={providersQuery.isLoading}
          environmentId={environmentId}
        />
      )}
    </Formik>
  );

  async function onSubmit(values: ContainerInstanceFormValues) {
    // find the subnet
    const isUsingSubnet = !values.allocatePublicIP && !!values.subnet;
    const vnWithSubnet = isUsingSubnet
      ? virtualNetworks.find((vn) => vn.id === values.virtualNetwork)
      : undefined;
    const subnet = vnWithSubnet?.properties.subnets.find(
      (sn) => sn.id === values.subnet
    );
    try {
      await mutateAsync({ values, subnet });
      notifySuccess('Container successfully created', values.name);
      router.stateService.go('azure.containerinstances');
    } catch (e) {
      notifyError('Failure', e as Error, 'Unable to create container');
    }
  }
}
